<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title style="font-size: 16px;">
        {{ title }}
      </b-card-title>
      <!-- datepicker -->
      <div class="d-flex align-items-center">
        <b-button-group
          v-model="selectedRange"
          class="mt-1 mt-sm-0"
          buttons
        >
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-primary"
            :pressed="selectedRange === 'monthly'"
            value="monthly"
            @click="selectedRange = 'monthly'"
          >
            {{ $t('Last month') }}
          </b-button>

          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-primary"
            :pressed="selectedRange === 'yearly'"
            value="yearly"
            @click="selectedRange = 'yearly'"
          >
            {{ $t('Last year') }}
          </b-button>
        </b-button-group>
      </div>
      <!-- datepicker -->
    </b-card-header>

    <!-- chart -->
    <b-card-body>
      <vue-apex-charts
        :key="selectedRange"
        type="bar"
        height="400"
        :options="chartOptions"
        :series="chartData"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BButton, BButtonGroup, BCard, BCardHeader, BCardBody, BCardTitle,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import VueApexCharts from 'vue-apexcharts'

export default {
  directives: {
    Ripple,
  },
  components: {
    BButton,
    BButtonGroup,
    BCard,
    BCardHeader,
    BCardBody,
    BCardTitle,
    VueApexCharts,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
    yStep: {
      type: Number,
      default: 5,
    },
  },
  data() {
    return {
      selectedRange: 'yearly',
    }
  },
  computed: {
    allSortedDates() {
      const dataset = this.data[this.selectedRange] || {}
      return Object.keys(Object.values(dataset)[0] || {}).sort(
        (a, b) => new Date(a.split('-').reverse().join('-')) - new Date(b.split('-').reverse().join('-')),
      )
    },
    chartData() {
      const dataset = this.data[this.selectedRange] || {}

      return Object.keys(dataset).map(key => ({
        name: (key === 'paid') ? this.$t('paid') : this.$t('with scholarship'),
        data: this.allSortedDates.map(date => dataset[key][date] || 0),
      }))
    },
    chartOptions() {
      const dataset = this.data[this.selectedRange] || {}
      const dates = this.allSortedDates

      const stackedSums = dates.map(date => Object.keys(dataset).reduce((sum, key) => {
        const val = dataset[key]?.[date] || 0
        return sum + val
      }, 0))

      const maxValue = Math.ceil(Math.max(...stackedSums, 1) / this.yStep) * this.yStep

      return {
        chart: {
          stacked: true,
          toolbar: {
            show: false,
          },
        },
        colors: ['#6CAFBC', '#D46878'],
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: true,
          position: 'top',
          fontSize: '14px',
          fontFamily: 'Manrope',
          horizontalAlign: 'left',
        },
        stroke: {
          show: true,
          colors: ['transparent'],
        },
        grid: {
          xaxis: {
            lines: {
              show: true,
            },
          },
        },
        xaxis: {
          categories: this.allSortedDates,
        },
        yaxis: {
          min: 0,
          max: maxValue,
          tickAmount: this.yStep ? maxValue / this.yStep : 4,
          forceNiceScale: false,
          labels: {
            formatter(value) {
              return Number.isInteger(value) ? value : ''
            },
          },
        },
        fill: {
          opacity: 1,
        },
      }
    },
  },
}
</script>

<style lang="scss">
  @import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
